import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useRef, useEffect } from 'preact/hooks';
import { Sparkles, Loader2, CheckCircle2, AlertCircle, Clock, AlertTriangle } from 'lucide-preact';
import { leadsApi } from '@/api/leads';
import { audienceApi } from '@/api/audience';
import { useToast } from '@/hooks/useToast';
/**
 * Reusable component for displaying enrichment status with retry capabilities.
 * Shows enrichment state and allows retry for failed enrichments.
 */
export const EnrichmentStatusCell = ({ enrichmentStatus, enrichedAt, enrichmentAvailableDate, enrichmentAttemptCount, id, clientCompanyId, workspaceId, campaignId, context = 'lead', onEnrichmentUpdated }) => {
    const [isEnriching, setIsEnriching] = useState(false);
    const abortControllerRef = useRef(null);
    const { showSuccess, showError } = useToast();
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);
    const canEnrich = !enrichmentAvailableDate || new Date(enrichmentAvailableDate) <= new Date();
    const isFailed = enrichmentStatus === 'Failed';
    const isSuccess = enrichmentStatus === 'Success';
    const isPending = enrichmentStatus === 'Pending';
    const isNeedsReview = enrichmentStatus === 'NeedsReview';
    const isNotStarted = enrichmentStatus === 'NotStarted' || !enrichmentStatus;
    const handleEnrich = async (e) => {
        e.stopPropagation();
        if (!canEnrich || isEnriching)
            return;
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsEnriching(true);
        // First, optimistically update the status to show enriching state immediately
        onEnrichmentUpdated?.({
            EnrichmentStatus: 'Pending',
            EnrichedAt: null,
            EnrichmentAvailableDate: null,
            EnrichmentAttemptCount: enrichmentAttemptCount + 1
        });
        try {
            console.log('[EnrichmentStatusCell] Re-enrich:', {
                context,
                workspaceId,
                campaignId,
                id,
                url: context === 'fileImportRow'
                    ? `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/FileImportRows/${id}/re-enrich`
                    : `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${id}/re-enrich`
            });
            const resp = context === 'fileImportRow'
                ? await audienceApi.reEnrich(clientCompanyId, workspaceId, campaignId, id, abortController.signal)
                : await leadsApi.reEnrich(workspaceId, campaignId, id, abortController.signal);
            // API returns 202 Accepted - enrichment is queued
            if (resp.status === 202) {
                showSuccess('Enrichment queued', 'Contact data enrichment has been requested');
            }
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError')
                return;
            // Revert status on error
            onEnrichmentUpdated?.({
                EnrichmentStatus: 'Failed',
                EnrichedAt: null,
                EnrichmentAvailableDate: null,
                EnrichmentAttemptCount: enrichmentAttemptCount
            });
            showError('Failed to queue enrichment', 'Please try again');
        }
        finally {
            if (!abortController.signal.aborted) {
                setIsEnriching(false);
            }
        }
    };
    // Show nothing if not started and no attempts (will be auto-queued)
    if (isNotStarted && enrichmentAttemptCount === 0) {
        return _jsx("span", { class: "text-slate-400 dark:text-slate-500", children: "\u2014" });
    }
    return (_jsxs("div", { class: "flex items-center gap-2", children: [isEnriching && (_jsxs("div", { class: "flex items-center gap-1 text-blue-600 dark:text-blue-400", children: [_jsx(Loader2, { class: "w-4 h-4 animate-spin" }), _jsx("span", { class: "text-xs", children: "Enriching..." })] })), !isEnriching && isSuccess && (_jsxs("div", { class: "flex items-center gap-1 text-green-600 dark:text-green-400", title: enrichedAt ? `Enriched on ${new Date(enrichedAt).toLocaleString()}` : 'Successfully enriched', children: [_jsx(CheckCircle2, { class: "w-4 h-4" }), _jsx("span", { class: "text-xs", children: "Enriched" })] })), !isEnriching && isFailed && (_jsxs("div", { class: "flex items-center gap-1", children: [_jsxs("div", { class: "flex items-center gap-1 text-red-600 dark:text-red-400", title: `Failed (${enrichmentAttemptCount} attempt${enrichmentAttemptCount !== 1 ? 's' : ''})`, children: [_jsx(AlertCircle, { class: "w-4 h-4" }), _jsx("span", { class: "text-xs", children: "Failed" })] }), canEnrich && (_jsx("button", { onClick: handleEnrich, class: "px-2 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors", title: "Retry enrichment", children: "Retry" })), !canEnrich && enrichmentAvailableDate && (_jsx("span", { class: "text-xs text-slate-500 dark:text-slate-400", title: `Available ${new Date(enrichmentAvailableDate).toLocaleString()}`, children: "(wait)" }))] })), !isEnriching && isPending && canEnrich && (_jsxs("div", { class: "flex items-center gap-1 text-blue-600 dark:text-blue-400", title: "Enrichment in progress", children: [_jsx(Sparkles, { class: "w-4 h-4" }), _jsx("span", { class: "text-xs", children: "Enriching..." })] })), !isEnriching && isPending && !canEnrich && enrichmentAvailableDate && (_jsxs("div", { class: "flex items-center gap-1 text-slate-600 dark:text-slate-400", title: `Available ${new Date(enrichmentAvailableDate).toLocaleString()}`, children: [_jsx(Clock, { class: "w-4 h-4" }), _jsx("span", { class: "text-xs", children: "Pending" })] })), !isEnriching && isNeedsReview && (() => {
                return (_jsxs("div", { class: "flex items-center gap-1", children: [_jsxs("div", { class: "flex items-center gap-1 text-amber-600 dark:text-amber-400", title: "Enrichment needs review", children: [_jsx(AlertTriangle, { class: "w-4 h-4" }), _jsx("span", { class: "text-xs", children: "Needs Review" })] }), canEnrich && (_jsx("button", { onClick: handleEnrich, class: "px-2 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors", title: "Re-enrich contact", children: "Re-enrich" }))] }));
            })()] }));
};
